import { useEffect, useState } from "react";
import { addDays, format, parseISO } from "date-fns";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { CATEGORIES, catStyle } from "@/lib/constants";
import { fmtDuration, toDay, toLocal, weekStartOf } from "@/lib/time";

const MEETING = "repeating-linear-gradient(135deg,#D9D2C3,#D9D2C3 4px,#EDE8DD 4px,#EDE8DD 8px)";
const hrs = (m) => fmtDuration(Math.round(m));

function WeekBar({ w, testId }) {
  const booked = Object.values(w.by_category).reduce((a, b) => a + b, 0) + w.meetings_minutes;
  const cap = Math.max(w.available_minutes, booked, 1);
  const pct = w.available_minutes ? Math.round((booked / w.available_minutes) * 100) : 0;
  const tone = pct >= 100 ? "text-red-600" : pct >= 85 ? "text-amber-600" : "text-[#103080]";
  return (
    <div className="min-w-0" data-testid={testId}>
      <div className="flex items-baseline justify-between gap-2 mb-1.5">
        <span className={`text-sm font-semibold ${tone}`}>{hrs(booked)} <span className="text-[#0F172A]/40 font-normal">/ {hrs(w.available_minutes)}</span></span>
        <span className={`text-xs font-semibold ${tone}`}>{pct}%</span>
      </div>
      <div className="h-3.5 rounded-full bg-[#F5F2EB] border border-[#103080]/10 overflow-hidden flex">
        {w.meetings_minutes > 0 && <div title={`Meetings ${hrs(w.meetings_minutes)}`} style={{ width: `${(w.meetings_minutes / cap) * 100}%`, background: MEETING }} />}
        {Object.entries(w.by_category).map(([cat, m]) => (
          <div key={cat} title={`${cat} ${hrs(m)}`} className="transition-[width] duration-500" style={{ width: `${(m / cap) * 100}%`, background: catStyle(cat).border }} />
        ))}
      </div>
    </div>
  );
}

function MemberRow({ m }) {
  return (
    <li className="grid grid-cols-1 md:grid-cols-[180px_repeat(4,minmax(0,1fr))] gap-4 md:gap-6 items-center py-5 border-b border-[#103080]/10 last:border-0" data-testid={`workload-member-${m.user_id}`}>
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-10 h-10 rounded-full bg-[#103080]/[0.07] text-[#103080] font-serif-brand font-semibold flex items-center justify-center shrink-0">{m.name.slice(0, 1).toUpperCase()}</div>
        <div className="min-w-0">
          <p className="font-semibold text-[#103080] truncate">{m.name}</p>
          {m.overflow_minutes > 0 && (
            <p className="text-[11px] text-red-600 font-semibold inline-flex items-center gap-1" data-testid={`workload-overflow-${m.user_id}`}>
              <AlertTriangle size={11} />{hrs(m.overflow_minutes)} won't fit
            </p>
          )}
        </div>
      </div>
      {m.weeks.map((w, i) => <WeekBar key={w.week_start} w={w} testId={`workload-week-${m.user_id}-${i}`} />)}
    </li>
  );
}

export default function Workload() {
  const [data, setData] = useState(null);
  const start = weekStartOf(new Date());

  useEffect(() => {
    api.get("/workload", { params: { start: toDay(start), now: toLocal(new Date()), weeks: 4 } })
      .then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const weeks = [0, 1, 2, 3].map((i) => addDays(start, 7 * i));
  return (
    <div className="space-y-8 fade-up" data-testid="workload-page">
      <div>
        <p className="text-xs uppercase tracking-[0.3em] text-[#0090D0] font-semibold mb-3">Team workload</p>
        <h1 className="font-serif-brand font-semibold text-[#103080] text-4xl sm:text-5xl">Who has <i className="text-[#0090D0]">room</i> this month</h1>
        <p className="mt-3 text-[#0F172A]/60 max-w-2xl">Booked time against working hours for the next 4 weeks. Booked time includes meetings plus scheduled tasks, projected forward in deadline order. This week counts from now.</p>
      </div>
      <section className="rounded-2xl border border-[#103080]/10 bg-white/70 ambient px-6" data-testid="workload-table">
        <div className="hidden md:grid grid-cols-[180px_repeat(4,minmax(0,1fr))] gap-6 py-4 border-b border-[#103080]/10 text-[11px] uppercase tracking-[0.2em] font-semibold text-[#103080]/60">
          <span>Team member</span>
          {weeks.map((w, i) => <span key={i}>{i === 0 ? "This week" : `w/c ${format(w, "d MMM")}`}</span>)}
        </div>
        {!data ? <p className="py-10 text-center text-[#103080]/50">Loading…</p> : (
          <ul>{data.map((m) => <MemberRow key={m.user_id} m={m} />)}</ul>
        )}
      </section>
      <div className="flex flex-wrap gap-x-4 gap-y-2" data-testid="workload-legend">
        <span className="inline-flex items-center gap-1.5 text-xs text-[#0F172A]/70"><span className="w-3 h-3 rounded-[4px]" style={{ background: MEETING }} />Meetings</span>
        {Object.entries(CATEGORIES).map(([name, c]) => (
          <span key={name} className="inline-flex items-center gap-1.5 text-xs text-[#0F172A]/70"><span className="w-3 h-3 rounded-[4px]" style={{ background: c.border }} />{name}</span>
        ))}
      </div>
      {data && <p className="text-xs text-[#0F172A]/40">Projected from {format(parseISO(toDay(start)), "d MMM yyyy")}. Job card tasks count once they're added to a schedule.</p>}
    </div>
  );
}
